/**
 * The Activity component will render either a Worksheet, Quiz, or GradedActivity.
 * If the quiz/worksheet is already finished at construction time, it will
 * render a GradedActivity component.  Otherwise it will render a Quiz/Worksheet component and
 * keep a state to allow the student to review the explanation of the last question before jumping
 * to results.
 */

import React, { Component } from 'react';
import * as PropTypes from 'prop-types';
import Quiz from './Quiz';
import Worksheet from './Worksheet';
import GradedActivity from './GradedActivity';
import ResearchViewer from '../../containers/ResearchViewer';
import { HORIZONTAL, VERTICAL } from '../../constants/common';
import {
  PANE_QUIZ_ORIENTATION,
  PANE_QUIZ_SIZE,
  PANE_WORKSHEET_ORIENTATION,
  PANE_WORKSHEET_SIZE,
} from '../../constants/preferences';
import { SplitPane } from '../Common/SplitPane';

const Activity = ({
  category,

  code,
  name,
  prefs,

  questions,
  responses,

  grade,
  gameMode,
  showQuizSummary,

  chooseAnswer,
  finishExercise,
  savePreference,
  retakeExercise,
}) => {
  const [showResults, setShowResults] = React.useState(grade.isComplete);
  React.useEffect(() => {
    /* Update showResults when viewing different exercise */
    setShowResults(grade.isComplete);
  }, [code]);

  /* If complete, render the graded activity page instead */
  if (showResults && grade.isComplete) {
    return (
      <GradedActivity
        name={name}
        code={code}
        questions={questions}
        responses={responses}
        grade={grade}
        gameMode={gameMode}
        retakeExercise={retakeExercise}
        showQuizSummary={showQuizSummary}
      />
    );
  }

  /* If incomplete, render quiz or worksheet element */
  const layoutPrefs = category === 'quiz' ? prefs.quiz : prefs.worksheet;
  return (
    <div className="flex-grow-1 position-relative">
      <SplitPane split={layoutPrefs.orientation} defaultSize={layoutPrefs.size}>
        <ResearchViewer />

        {category === 'worksheet' ? (
          <Worksheet
            code={code}
            name={name}
            questions={questions}
            responses={responses}
            grade={grade}
            chooseAnswer={chooseAnswer}
            finishExercise={finishExercise}
            orientation={layoutPrefs.orientation}
            setOrientation={(orientation) =>
              savePreference(PANE_WORKSHEET_ORIENTATION, orientation)
            }
            showResults={() => setShowResults(true)}
          />
        ) : (
          <Quiz
            code={code}
            name={name}
            questions={questions}
            responses={responses}
            grade={grade}
            chooseAnswer={chooseAnswer}
            finishExercise={finishExercise}
            orientation={layoutPrefs.orientation}
            setOrientation={(orientation) =>
              savePreference(PANE_QUIZ_ORIENTATION, orientation)
            }
            showResults={() => setShowResults(true)}
          />
        )}
      </SplitPane>
    </div>
  );
};

Activity.propTypes = {
  category: PropTypes.oneOf(['quiz', 'worksheet']),

  code: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,

  prefs: PropTypes.shape({
    quiz: PropTypes.shape({
      size: PropTypes.number,
      orientation: PropTypes.string,
    }),
    worksheet: PropTypes.shape({
      size: PropTypes.number,
      orientation: PropTypes.string,
    }),
  }),

  questions: PropTypes.array,
  responses: PropTypes.array,

  grade: PropTypes.shape({
    id: PropTypes.number,
    exerciseCode: PropTypes.string,
    score: PropTypes.number,
    timeOnTask: PropTypes.number,
    attempts: PropTypes.number,
    isComplete: PropTypes.bool,
    retakesAllowed: PropTypes.number,
  }),

  savePreference: PropTypes.func.isRequired,
};
Activity.defaultProps = {
  grade: {
    id: 0,
    exerciseCode: '',
    score: undefined,
    timeOnTask: 0,
    attempts: 0,
    isComplete: false,
  },
};

export default Activity;
